import React, { useMemo, useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, ImageField, Modal, SummaryList, useSession, useToast } from '../ui.jsx';
import { centsToInput, dateLabel, money, monthLabel, nameOf, parseAmountToCents, splitEvenly } from '../lib/format.js';
import { navigate } from '../main.jsx';

const today = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

/**
 * 發出付款項目. There is no draft state: the administrator reviews a full summary
 * and one final confirmation creates the 付款項目, every 應付款 and the audit event.
 */
export default function IssueItemPage() {
  const session = useSession();
  const notify = useToast();
  const payingMembers = session.members.filter((member) => member.status === 'active');

  const [form, setForm] = useState(() => ({
    unit_id: session.all_units[0] ? session.all_units[0].id : '',
    title: '',
    category: '租金',
    accounting_month: today().slice(0, 7),
    amount_kind: 'fixed',
    total: '',
    due_date: today(),
    recipient_kind: 'external',
    recipient_member_id: payingMembers[0] ? payingMembers[0].id : '',
    recipient_name: '',
    confirmer_member_id: '',
    equipment_name: '',
  }));
  const [selected, setSelected] = useState(() => payingMembers.map((member) => member.id));
  const [custom, setCustom] = useState({});
  const [customMode, setCustomMode] = useState(false);
  const [billProof, setBillProof] = useState(null);
  const [billProofId, setBillProofId] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const totalCents = parseAmountToCents(form.total);

  const allocations = useMemo(() => {
    if (totalCents === null) return [];
    if (customMode) {
      return selected.map((memberId) => ({
        member_id: memberId,
        amount_cents: parseAmountToCents(custom[memberId] ?? ''),
      }));
    }
    const shares = splitEvenly(totalCents, selected.length);
    return selected.map((memberId, index) => ({ member_id: memberId, amount_cents: shares[index] }));
  }, [totalCents, customMode, custom, selected]);

  const allocationSum = allocations.reduce((total, allocation) => total + (allocation.amount_cents || 0), 0);
  const externalConfirmerRequired = form.recipient_kind === 'external';
  const confirmerCandidates = payingMembers.filter((member) => !selected.includes(member.id));
  const problems = [];
  if (!form.title.trim()) problems.push('必須輸入項目名稱');
  if (totalCents === null || totalCents <= 0) problems.push('總額必須是大於 0 的港幣金額');
  if (!selected.length) problems.push('必須至少選擇一名分攤成員');
  if (allocations.some((allocation) => !allocation.amount_cents)) problems.push('每名分攤成員都需要大於 0 的金額');
  if (totalCents !== null && allocationSum !== totalCents) problems.push('各成員分攤合計必須等於總額');
  if (form.recipient_kind === 'external' && !form.recipient_name.trim()) problems.push('必須輸入外部收款人名稱');
  if (externalConfirmerRequired && !form.confirmer_member_id) problems.push('外部收款人必須指定一名非付款成員為確認人');
  if (form.recipient_kind === 'member' && selected.includes(form.recipient_member_id) && selected.length === 1) {
    problems.push('收款人不能是唯一的分攤成員');
  }

  const confirmerLabel = form.recipient_kind === 'member'
    ? `${nameOf(session.members, form.recipient_member_id)}（群組內收款人自然擔任確認人）`
    : nameOf(session.members, form.confirmer_member_id);

  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        unit_id: form.unit_id,
        title: form.title.trim(),
        category: form.category,
        accounting_month: form.accounting_month,
        total_cents: totalCents,
        amount_kind: form.amount_kind,
        due_date: form.due_date,
        allocations,
        recipient: form.recipient_kind === 'member'
          ? { kind: 'member', member_id: form.recipient_member_id }
          : { kind: 'external', name: form.recipient_name.trim() },
        confirmer_member_id: form.recipient_kind === 'external' ? form.confirmer_member_id : undefined,
        bill_proof_id: billProofId,
        equipment: form.equipment_name.trim() ? { name: form.equipment_name.trim() } : undefined,
      };
      const result = await api.issueItem(payload);
      notify('付款項目已發出，各成員應付款即時生效');
      navigate(`/items/${result.item.id}`);
    } catch (failure) {
      setError(failure);
      setSummaryOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const uploadBillProof = async (file) => {
    setBillProof(file);
    setBillProofId(null);
    if (!file) return;
    try {
      const formData = new FormData();
      formData.set('image', file);
      const result = await api.uploadBillProof(formData);
      setBillProofId(result.bill_proof.id);
      notify('帳單證明已上傳');
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>發出付款項目</h1>
          <p className="small muted">MVP 不設草稿：確認摘要後一次操作即建立項目及各成員應付款。</p>
        </div>
      </div>

      <ErrorBanner error={error} />

      <Card title="項目內容">
        <div className="grid form">
          <label className="field">
            <span>單位</span>
            <select value={form.unit_id} onChange={(event) => update('unit_id', event.target.value)} data-testid="issue-unit">
              {session.all_units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>分類</span>
            <select value={form.category} onChange={(event) => update('category', event.target.value)} data-testid="issue-category">
              {session.categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label className="field">
            <span>帳務月份</span>
            <input type="month" value={form.accounting_month} onChange={(event) => update('accounting_month', event.target.value)} data-testid="issue-month" />
          </label>
          <label className="field">
            <span>到期日</span>
            <input type="date" value={form.due_date} onChange={(event) => update('due_date', event.target.value)} data-testid="issue-due-date" />
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            <span>項目名稱</span>
            <input value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="例如：8 月泥圍租金" data-testid="issue-title" />
          </label>
          <label className="field">
            <span>金額類型</span>
            <select value={form.amount_kind} onChange={(event) => update('amount_kind', event.target.value)} data-testid="issue-amount-kind">
              <option value="fixed">固定金額</option>
              <option value="floating">浮動金額（可附帳單證明）</option>
            </select>
          </label>
          <label className="field">
            <span>總額（HKD）</span>
            <input value={form.total} onChange={(event) => update('total', event.target.value)} inputMode="decimal" data-testid="issue-total" />
          </label>
          <label className="field">
            <span>設備名稱（選填，會建立設備紀錄）</span>
            <input value={form.equipment_name} onChange={(event) => update('equipment_name', event.target.value)} data-testid="issue-equipment" />
          </label>
        </div>

        {form.amount_kind === 'floating' ? (
          <fieldset style={{ marginTop: 12 }}>
            <legend>帳單證明</legend>
            <p className="small muted">帳單證明用來說明項目總額，不代表任何成員已付款；成員的付款證明另行提交。</p>
            <ImageField label="帳單圖片" file={billProof} onChange={uploadBillProof} testId="issue-bill-proof" />
            {billProofId ? <p className="small" data-testid="bill-proof-ready">帳單證明已就緒（{billProofId}）</p> : null}
          </fieldset>
        ) : null}
      </Card>

      <Card title="收款人與確認人">
        <div className="grid form">
          <label className="field">
            <span>收款人類型</span>
            <select value={form.recipient_kind} onChange={(event) => update('recipient_kind', event.target.value)} data-testid="issue-recipient-kind">
              <option value="external">群組以外（例如業主）</option>
              <option value="member">群組成員（代付者）</option>
            </select>
          </label>
          {form.recipient_kind === 'member' ? (
            <label className="field">
              <span>收款成員</span>
              <select value={form.recipient_member_id} onChange={(event) => update('recipient_member_id', event.target.value)} data-testid="issue-recipient-member">
                {payingMembers.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
              </select>
            </label>
          ) : (
            <>
              <label className="field">
                <span>外部收款人名稱</span>
                <input value={form.recipient_name} onChange={(event) => update('recipient_name', event.target.value)} data-testid="issue-recipient-name" />
              </label>
              <label className="field">
                <span>確認人（必須是非付款成員）</span>
                <select value={form.confirmer_member_id} onChange={(event) => update('confirmer_member_id', event.target.value)} data-testid="issue-confirmer">
                  <option value="">請選擇</option>
                  {confirmerCandidates.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
                </select>
              </label>
            </>
          )}
        </div>
        <p className="small muted" style={{ marginTop: 8 }}>確認人：{confirmerLabel || '—'}</p>
      </Card>

      <Card
        title="成員分攤"
        actions={(
          <button type="button" className="small" onClick={() => setCustomMode(!customMode)} data-testid="issue-toggle-custom">
            {customMode ? '改用平均分攤' : '改用自訂金額'}
          </button>
        )}
      >
        <div className="stack">
          {payingMembers.map((member) => {
            const isSelected = selected.includes(member.id);
            const allocation = allocations.find((entry) => entry.member_id === member.id);
            return (
              <div className="row" key={member.id} style={{ justifyContent: 'space-between' }}>
                <label className="row small" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={isSelected}
                    data-testid={`issue-member-${member.display_name}`}
                    onChange={(event) => setSelected(event.target.checked
                      ? [...selected, member.id]
                      : selected.filter((id) => id !== member.id))}
                  />
                  {member.display_name}
                  {member.role === 'member' ? <span className="chip">一般成員</span> : null}
                </label>
                {isSelected ? (
                  customMode ? (
                    <input
                      style={{ maxWidth: 140 }}
                      value={custom[member.id] ?? centsToInput(allocation ? allocation.amount_cents : 0)}
                      data-testid={`issue-allocation-${member.display_name}`}
                      onChange={(event) => setCustom({ ...custom, [member.id]: event.target.value })}
                    />
                  ) : (
                    <span className="amount" data-testid={`issue-share-${member.display_name}`}>
                      {allocation ? money(allocation.amount_cents) : '—'}
                    </span>
                  )
                ) : <span className="muted small">不分攤</span>}
              </div>
            );
          })}
          <div className="spread">
            <span className="muted small">分攤合計</span>
            <strong className="amount" data-testid="issue-allocation-sum">{money(allocationSum)}</strong>
          </div>
          <p className="small muted">金額以整數港仙處理；平均分攤時除不盡的仙位由列表第一位成員承擔。</p>
        </div>
      </Card>

      {problems.length ? (
        <div className="banner warn small" data-testid="issue-problems">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {problems.map((problem) => <li key={problem}>{problem}</li>)}
          </ul>
        </div>
      ) : null}

      <div className="actions">
        <button type="button" className="primary" disabled={problems.length > 0} onClick={() => setSummaryOpen(true)} data-testid="issue-review">
          檢視摘要
        </button>
      </div>

      {summaryOpen ? (
        <Modal title="發出前確認摘要" subtitle="確認後會即時建立付款項目及各成員應付款" onClose={() => setSummaryOpen(false)}>
          <SummaryList rows={[
            ['單位', session.all_units.find((unit) => unit.id === form.unit_id)?.name],
            ['項目名稱', form.title],
            ['分類', form.category],
            ['帳務月份', monthLabel(form.accounting_month)],
            ['到期日', dateLabel(form.due_date)],
            ['總額', money(totalCents)],
            ['金額類型', form.amount_kind === 'floating' ? '浮動金額' : '固定金額'],
            ['帳單證明', billProofId ? '已附' : '無'],
            ['收款人', form.recipient_kind === 'member' ? nameOf(session.members, form.recipient_member_id) : form.recipient_name],
            ['確認人', confirmerLabel],
            form.equipment_name.trim() ? ['設備紀錄', form.equipment_name.trim()] : null,
          ]} />
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table>
              <thead><tr><th>成員</th><th className="num">應付款</th></tr></thead>
              <tbody>
                {allocations.map((allocation) => (
                  <tr key={allocation.member_id}>
                    <td>{nameOf(session.members, allocation.member_id)}</td>
                    <td className="num">{money(allocation.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td>合計</td><td className="num">{money(allocationSum)}</td></tr></tfoot>
            </table>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={() => setSummaryOpen(false)}>返回修改</button>
            <button type="button" className="primary" disabled={busy} onClick={issue} data-testid="issue-confirm">確認並發出</button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
